import { AfterViewInit, Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthController } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    RaButtonComponent,
    RaInputComponent,
    RaLoadingComponent,
    RaToastService,
} from '@shared/components';
import { TestIdDirective } from '@shared/directives';
import { RaIconComponent } from 'ra-ui';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { AppleIdentityService } from 'src/app/shared/services/apple-identity.service';
import { GoogleIdentityService } from 'src/app/shared/services/google-identity.service';
import { AuthCardLayoutComponent } from '../../components/auth-card-layout/auth-card-layout.component';
import { BannerComponent } from '../../components/banner/banner.component';

@Component({
    selector: 'login',
    templateUrl: 'login.page.html',
    styleUrl: 'login.page.scss',
    imports: [
        ReactiveFormsModule,
        RaInputComponent,
        RaButtonComponent,
        TestIdDirective,
        BannerComponent,
        AuthCardLayoutComponent,
        RouterLink,
        TranslatePipe,
        RaIconComponent,
        RaLoadingComponent,
    ],
})
export class LoginPage implements OnInit, AfterViewInit {
    @ViewChild('googleButtonHost') private _googleButtonHost?: ElementRef<HTMLDivElement>;
    public formGroup: FormGroup;
    public loading = signal(false);
    public appleLoading = signal(false);
    public isPasswordVisible = signal(true);

    // * Injects
    private _router = inject(Router);
    private _utilService = inject(UtilService);
    private _authController = inject(AuthController);
    private _googleIdentityService = inject(GoogleIdentityService);
    private _appleIdentityService = inject(AppleIdentityService);
    private _raToastService = inject(RaToastService);
    private _translate = inject(TranslateService);

    /**
     * Só esconde o botão quando a config do Firebase ainda não foi preenchida no
     * environment — é a única forma de o fluxo não existir. Não há gate por
     * plataforma/sistema operacional no web.
     */
    public readonly isAppleAvailable = this._appleIdentityService.isConfigured;

    ngOnInit(): void {
        this._initFormLogin();
    }

    ngAfterViewInit(): void {
        this._googleIdentityService
            .renderSignInButton(this._googleButtonHost?.nativeElement, idToken =>
                this.loginWithGoogle(idToken)
            )
            .catch(() => null);
    }

    /**
     * Initialize the form login
     */
    private _initFormLogin(): void {
        this.formGroup = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            password: new FormControl('', [Validators.required]),
        });
    }

    /**
     * Login the user
     */
    public login(): void {
        if (!this._utilService.isFormValid(this.formGroup) || this.loading()) return;

        this.loading.set(true);
        this._authController
            .login(this.formGroup.value.email, this.formGroup.value.password)
            .subscribe({
                next: user => {
                    this._authController.navigateAfterAuth(user);
                    this.loading.set(false);
                },
                error: () => this.loading.set(false),
            });
    }

    public loginWithGoogle(idToken: string): void {
        if (!idToken || this.loading()) return;

        this.loading.set(true);
        this._authController.loginWithGoogle(idToken).subscribe({
            next: user => {
                this._authController.navigateAfterAuth(user);
                this.loading.set(false);
            },
            error: () => this.loading.set(false),
        });
    }

    /**
     * Login com Apple.
     *
     * O popup do Firebase devolve o Firebase ID token (formato que o backend
     * espera) e, só na PRIMEIRA autorização do Apple ID, o nome. Cancelar o popup
     * não é erro: o serviço devolve `null` e o fluxo só volta ao estado inicial.
     */
    public async loginWithApple(): Promise<void> {
        if (this.loading() || this.appleLoading()) return;

        this.appleLoading.set(true);
        this.loading.set(true);

        try {
            const result = await this._appleIdentityService.signInAndGetIdToken();
            if (!result) {
                this._resetAppleLoading();
                return;
            }

            this._authController
                .loginWithApple(result.idToken, result.firstName, result.lastName)
                .subscribe({
                    next: user => {
                        this._authController.navigateAfterAuth(user);
                        this._resetAppleLoading();
                    },
                    error: () => this._resetAppleLoading(),
                });
        } catch (error) {
            console.error('[LoginPage] Falha no Sign in with Apple', error);
            this._raToastService.error({
                message: this._translate.instant('auth.login.appleError'),
            });
            this._resetAppleLoading();
        }
    }

    private _resetAppleLoading(): void {
        this.appleLoading.set(false);
        this.loading.set(false);
    }

    /**
     * Navigate to the create account page
     */
    public navigateToCreateAccount(): void {
        // "Criar conta" sempre entra no wizard (welcome).
        this._router.navigate(['/auth/onboarding']);
    }
}
